import { duration } from '../utils/duration';

/**
 * Creative IDs for the A/B 0474 crate-waiting / FTUE messages. The id is both
 * the asset key and the value stored in `creativeAssetID` (rotated by the
 * picker). Kept here (not in the flex template) so the picker + tests don't pull
 * in the chatbot flex module graph. Locale-agnostic — the EN art is served to
 * all locales for now (see `crateWaitingAssets`).
 */
export const CRATE_WAITING_CREATIVE_IDS = [
  'crateWaiting/crateskipper_a1',
  'crateWaiting/crateskipper_a2',
  'crateWaiting/crateskipper_b1',
  'crateWaiting/crateskipper_b2',
  'crateWaiting/crateskipper_c1',
  'crateWaiting/crateskipper_c2',
  'crateWaiting/crateskipper_d1',
  'crateWaiting/crateskipper_d2',
  'crateWaiting/crateskipper_g1',
  'crateWaiting/crateskipper_g2',
  'crateWaiting/crateskipper_g3',
] as const;

export type CrateWaitingCreativeID =
  (typeof CRATE_WAITING_CREATIVE_IDS)[number];

/**
 * Final crate-skipper creatives, localized per `creativeAssetID`. Each creative
 * ships an `_en` and `_ja` asset; the registry key is `<creativeAssetID>/<locale>`
 * and the flex template resolves the recipient's locale (mirrors
 * `reengagementAssets`). `ja` is the default locale (see `getLocale`).
 */
export const crateWaitingAssets = {
  'crateWaiting/crateskipper_a1/en':
    'private/chatbot/crateWaiting/crateskipper_a1_en.png',
  'crateWaiting/crateskipper_a1/ja':
    'private/chatbot/crateWaiting/crateskipper_a1_ja.png',
  'crateWaiting/crateskipper_a2/en':
    'private/chatbot/crateWaiting/crateskipper_a2_en.png',
  'crateWaiting/crateskipper_a2/ja':
    'private/chatbot/crateWaiting/crateskipper_a2_ja.png',
  'crateWaiting/crateskipper_b1/en':
    'private/chatbot/crateWaiting/crateskipper_b1_en.png',
  'crateWaiting/crateskipper_b1/ja':
    'private/chatbot/crateWaiting/crateskipper_b1_ja.png',
  'crateWaiting/crateskipper_b2/en':
    'private/chatbot/crateWaiting/crateskipper_b2_en.png',
  'crateWaiting/crateskipper_b2/ja':
    'private/chatbot/crateWaiting/crateskipper_b2_ja.png',
  'crateWaiting/crateskipper_c1/en':
    'private/chatbot/crateWaiting/crateskipper_c1_en.png',
  'crateWaiting/crateskipper_c1/ja':
    'private/chatbot/crateWaiting/crateskipper_c1_ja.png',
  'crateWaiting/crateskipper_c2/en':
    'private/chatbot/crateWaiting/crateskipper_c2_en.png',
  'crateWaiting/crateskipper_c2/ja':
    'private/chatbot/crateWaiting/crateskipper_c2_ja.png',
  'crateWaiting/crateskipper_d1/en':
    'private/chatbot/crateWaiting/crateskipper_d1_en.png',
  'crateWaiting/crateskipper_d1/ja':
    'private/chatbot/crateWaiting/crateskipper_d1_ja.png',
  'crateWaiting/crateskipper_d2/en':
    'private/chatbot/crateWaiting/crateskipper_d2_en.png',
  'crateWaiting/crateskipper_d2/ja':
    'private/chatbot/crateWaiting/crateskipper_d2_ja.png',
  'crateWaiting/crateskipper_g1/en':
    'private/chatbot/crateWaiting/crateskipper_g1_en.png',
  'crateWaiting/crateskipper_g1/ja':
    'private/chatbot/crateWaiting/crateskipper_g1_ja.png',
  'crateWaiting/crateskipper_g2/en':
    'private/chatbot/crateWaiting/crateskipper_g2_en.png',
  'crateWaiting/crateskipper_g2/ja':
    'private/chatbot/crateWaiting/crateskipper_g2_ja.png',
  'crateWaiting/crateskipper_g3/en':
    'private/chatbot/crateWaiting/crateskipper_g3_en.png',
  'crateWaiting/crateskipper_g3/ja':
    'private/chatbot/crateWaiting/crateskipper_g3_ja.png',
} satisfies Record<string, string>;

/** Fixed JST slot hours. The list itself encodes quiet hours: there
 *  are no slots between 20:00 and 08:00, so "next future slot" never lands in
 *  the quiet window. */
export const CRATE_WAITING_SLOTS_JST = [8, 12, 16, 20] as const;

/** Active window bounds (JST hours) for the irregular first (+1h) message. */
export const CRATE_WAITING_ACTIVE_START_HOUR = 8;
export const CRATE_WAITING_ACTIVE_END_HOUR = 20;

/** Send crate-waiting messages only on D0-D2 (JST days since install). */
export const CRATE_WAITING_WINDOW_DAYS = 2;

/** Delay before the first crate-waiting message after first entry. */
export const CRATE_WAITING_FIRST_DELAY = duration({ hours: 1 });

/** Pool creatives not yet sent in the current cycle. */
export function getRemainingCrateWaitingCreatives(
  sentCreatives: readonly string[],
): CrateWaitingCreativeID[] {
  return CRATE_WAITING_CREATIVE_IDS.filter((id) => !sentCreatives.includes(id));
}
